import { Component, Input, Output, EventEmitter, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApiService } from '@core/services/api.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { ConfirmDialogService } from '@core/services/confirm-dialog.service';

@Component({
  standalone: true,
  selector: 'app-doc-attach',
  imports: [CommonModule, MatIconModule],
  templateUrl: './doc-attach.component.html',
  styleUrls: ['./doc-attach.component.scss'],
})
export class DocAttachComponent {
  private destroyRef = inject(DestroyRef);

  @Input() label = 'Attachment';
  @Input() docType = '';
  @Input() employeeId = '';
  @Input() doc: any = null;
  @Input() readonly = false;
  @Input() compact = false;
  @Input() deferrable = false;
  @Input() downloadPrefix = '';
  @Output() changed = new EventEmitter<void>();
  @Output() uploaded = new EventEmitter<any>();
  @Output() removed = new EventEmitter<void>();
  @Output() pendingChange = new EventEmitter<{ docType: string; file: File | null }>();

  uploading = false;
  deleting = false;
  pendingFile: File | null = null;

  previewOpen = false;
  previewLoading = false;
  previewRawUrl = '';
  previewSafeUrl: SafeResourceUrl | null = null;
  previewIsImage = false;
  previewIsPdf = false;

  constructor(
    private api: ApiService,
    private snackbar: SnackbarService,
    private confirmDialog: ConfirmDialogService,
    private sanitizer: DomSanitizer,
  ) {}

  get displayName(): string {
    const base = (this.doc?.fileName || this.pendingFile?.name || 'Attachment').split(/[\\/]/).pop() || 'Attachment';
    return base.replace(/^\d{6,}_/, '');
  }

  openPreview(): void {
    if (!this.doc?.id) return;
    this.previewOpen = true;
    this.previewLoading = true;
    this.api.get(`/employee-documents/${this.doc.id}/download`).pipe(
      catchError(() => of(null)), takeUntilDestroyed(this.destroyRef),
    ).subscribe((res: any) => {
      this.previewLoading = false;
      if (!res?.fileContent) { this.snackbar.showFailure('Could not load preview'); this.previewOpen = false; return; }
      const mime = res.mimeType || '';
      const bytes = this.b64ToBytes(res.fileContent);
      this.revokePreview();
      this.previewRawUrl = URL.createObjectURL(new Blob([bytes], { type: mime || 'application/octet-stream' }));
      this.previewSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.previewRawUrl);
      this.previewIsImage = mime.startsWith('image/');
      this.previewIsPdf = mime.includes('pdf');
    });
  }
  closePreview(): void { this.previewOpen = false; this.revokePreview(); }
  private revokePreview(): void {
    if (this.previewRawUrl) { URL.revokeObjectURL(this.previewRawUrl); this.previewRawUrl = ''; }
    this.previewSafeUrl = null; this.previewIsImage = false; this.previewIsPdf = false;
  }
  private b64ToBytes(b64: string): Uint8Array {
    const s = atob(b64); const a = new Uint8Array(s.length);
    for (let i = 0; i < s.length; i++) a[i] = s.charCodeAt(i);
    return a;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const file = input.files[0];
    input.value = '';
    if (!this.employeeId) {
      if (this.deferrable) {
        this.pendingFile = file;
        this.pendingChange.emit({ docType: this.docType, file });
      } else {
        this.snackbar.showFailure('Save the employee first, then attach this document');
      }
      return;
    }

    const fd = new FormData();
    fd.append('file', file);
    fd.append('documentType', this.docType);
    this.uploading = true;
    this.api.postForm(`/employee-documents/employee/${this.employeeId}/upload`, fd).pipe(
      catchError((err: any) => { this.snackbar.showFailure(err?.error?.message || 'Upload failed'); return of(null); }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((res: any) => {
      this.uploading = false;
      const created = res?.data || null;
      if (res?.success || created) {
        this.snackbar.showSuccess(this.label + ' uploaded');
        if (created) this.uploaded.emit(created);
        this.changed.emit();
      }
    });
  }

  download(): void {
    if (!this.doc?.id) return;
    this.api.get(`/employee-documents/${this.doc.id}/download`).pipe(
      catchError(() => { this.snackbar.showFailure('Download failed'); return of(null); }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((res: any) => {
      if (!res?.fileContent) return;
      const byteStr = atob(res.fileContent);
      const bytes = new Uint8Array(byteStr.length);
      for (let i = 0; i < byteStr.length; i++) bytes[i] = byteStr.charCodeAt(i);
      const blob = new Blob([bytes], { type: res.mimeType || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = this.downloadName(res.fileName); a.click();
      URL.revokeObjectURL(url);
    });
  }

  private downloadName(serverName: string): string {
    const base = (serverName || this.displayName || 'file').split(/[\\/]/).pop() || 'file';
    return (this.downloadPrefix || '') + base.replace(/^\d{6,}_/, '');
  }

  clearPending(): void {
    this.pendingFile = null;
    this.pendingChange.emit({ docType: this.docType, file: null });
  }

  async remove(): Promise<void> {
    if (!this.doc?.id) return;
    const ok = await this.confirmDialog.confirm(`Delete the ${this.label} attachment? This cannot be undone.`, {
      title: 'Delete Attachment', confirmLabel: 'Delete', danger: true,
    });
    if (!ok) return;
    this.deleting = true;
    this.api.delete(`/employee-documents/${this.doc.id}`).pipe(
      catchError(() => { this.snackbar.showFailure('Delete failed'); return of(null); }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((res: any) => {
      this.deleting = false;
      if (res?.success) {
        this.snackbar.showSuccess(this.label + ' removed');
        this.removed.emit();
        this.changed.emit();
      }
    });
  }
}
